// Governance cockpits — shared anatomy pieces for the B4 Compliance and
// B5 Quality Assurance work queues, so the two pages feel like siblings:
// summary strip tiles, the severity dot (fixed RAG semantics matching the
// loan tabs' health tokens) and the due/overdue delta text.

import { cn } from "@/design-system/lib/utils";
import { calendarDaysUntil } from "@/utils/datetime";
import type { WorkQueueSeverity } from "@/api/compliance/workQueueTypes";

export interface SummaryTile {
  id: string;
  label: string;
  value: number;
  tone?: "error" | "warning" | "success";
  /** Optional formatted rendering (e.g. currency) — the numeric `value`
   *  still drives the colorize-only-when-nonzero rule. */
  display?: string;
}

export function SummaryStrip({
  tiles,
  "data-testid": testId,
}: {
  tiles: SummaryTile[];
  "data-testid"?: string;
}) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-5 gap-space-3"
      data-testid={testId}
    >
      {tiles.map((t) => (
        <div
          key={t.id}
          className="bg-white-pure border border-border-light rounded-lg px-space-3 py-space-2"
          data-testid={`summary-tile-${t.id}`}
        >
          <div
            className={cn(
              "font-display font-bold text-h3 m-0 tabular-nums",
              t.tone === "error" && t.value !== 0 && "text-status-error",
              t.tone === "warning" && t.value !== 0 && "text-status-warning",
              t.tone === "success" && t.value !== 0 && "text-status-success",
            )}
          >
            {t.display ?? t.value}
          </div>
          <div className="text-caption text-gray-mid">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

export function SeverityDot({ severity }: { severity: WorkQueueSeverity }) {
  return (
    <span
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full shrink-0",
        severity === "error" ? "bg-status-error" : "bg-status-warning",
      )}
      role="img"
      aria-label={severity === "error" ? "Overdue or critical" : "Due soon"}
      data-severity={severity}
    />
  );
}

/**
 * Shared queue sort. "urgency" keeps the server's canonical order (errors
 * first, then due date, then title — the loan-feed sort); "due" and "loan"
 * re-sort client-side. ISO date strings compare correctly as strings.
 */
export function sortQueueRows<
  T extends { dueDate: string | null; loanNumber: string | null },
>(rows: T[], sort: string): T[] {
  if (sort === "due") {
    return [...rows].sort((a, b) =>
      (a.dueDate ?? "9999-12-31").localeCompare(b.dueDate ?? "9999-12-31"),
    );
  }
  if (sort === "loan") {
    return [...rows].sort((a, b) =>
      (a.loanNumber ?? "").localeCompare(b.loanNumber ?? ""),
    );
  }
  return rows;
}

/// "Overdue 3d" | "Due today" | "Due in 12d" | "—" for undated rows.
///
/// 🔴 `tz` IS THE VIEWER'S ZONE AND MUST BE PASSED. It was `undefined`, which
/// falls back to Phoenix — right for a Phoenix operator by accident, and wrong
/// for everyone else, who read a label resolved in a zone they are not in. It
/// matters most where a caller ALSO colours the cell: `DefaultsWorkoutCockpitPage`
/// painted the red/amber class from one rule and this label from another, so at
/// 18:00 the cell said "Due today" in overdue red. A colour-only fix leaves the
/// two halves split for a non-Phoenix viewer, which is why the zone had to move
/// with it.
///
/// This is a PURE MODULE FUNCTION — the zone arrives as an argument, never from
/// a hook, so it stays callable from a table-column factory. `today` stays
/// injectable for tests.
///
/// Parameter order mirrors `calendarDaysUntil(iso, tz, now)` deliberately: a
/// wrapper that orders its arguments differently from the thing it wraps is the
/// next mistake. No call site passes a second positional argument today, and
/// under `strict` a `Date` in the `tz` slot is a compile error rather than a
/// silent rebind.
export function dueDelta(dueDate: string | null, today = new Date()): string {
  if (!dueDate) return "—";
  // Was `Date.UTC(today.getUTC*())` — today read in UTC, so after 5pm in
  // Phoenix every row aged a day early and a due-today row read "Overdue 1d".
  // The DUE side was already right (sliced to its calendar day, parsed as UTC);
  // only "today" needed moving, and it moved to the OPERATIONAL day rather than
  // the viewer's so this row cannot contradict the server count above it.
  // `today` stays injectable.
  const days = calendarDaysUntil(dueDate.slice(0, 10), today);
  if (days === null) return "—";
  if (days < 0) return `Overdue ${-days}d`;
  if (days === 0) return "Due today";
  return `Due in ${days}d`;
}
